import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import flatMap from 'lodash/fp/flatMap';

import { useTranslation } from 'hooks';
import { selectProfile } from 'store/profile';
import { fetchStayReasons, selectAllStayReasons } from 'store/stayReasons';
import {
	fetchPreArrivalPreference,
	selectAllPreArrivalPreference,
	updatePreArrivalPreference,
} from 'store/preArrivalPreference';
import getInitialArrivalTime from 'utils/getInitialArrivalTime';
import { formatArrivalTime } from 'utils/utils';
import { BookingFlowLoadingIndicator } from 'BookingFlow/components';
import { arrivalDetailsFormCompleted } from 'store/analyticEvents';
import { TimeOfArrival, GuestInformation, ReasonForStay } from './ArrivalDetailsComponents';
import ArrivalDetailCards from './ArrivalDetailsComponents/ArrivalDetailCards';

export default function ArrivalDetails({ upcomingTrip, propertyContent }) {
	const { t } = useTranslation();
	const dispatch = useDispatch();

	const profile = useSelector(selectProfile);

	const preArrivalPreferencesRes = useSelector(selectAllPreArrivalPreference);
	const preArrivalPreferences = preArrivalPreferencesRes?.data;

	// Initial load fetch pre-arrival preferences
	useEffect(() => {
		dispatch(fetchPreArrivalPreference({ bookingId: upcomingTrip.bookingId }));
	}, [dispatch, upcomingTrip.bookingId]);

	const stayReasonsRes = useSelector(selectAllStayReasons);
	const stayReasons = stayReasonsRes?.data || [];
	const stayReasonsAllOptions = flatMap(({ options }) => options)(stayReasons);

	// Initial load fetch stay reasons for the checkboxes
	useEffect(() => {
		dispatch(fetchStayReasons());
	}, [dispatch]);

	// Control the form and steps
	const [formStep, setFormStep] = useState(0);

	const goToFormStep = step => {
		setFormStep(step);
	};

	// State for the form values, should only be used when hasArrivalDetails is false
	const [arrivalDetailsValues, setArrivalDetailsValues] = useState({
		guestEta: getInitialArrivalTime(propertyContent)(upcomingTrip),
		stayReasonIds: [],
		guests: [],
		comments: '',
	});

	// Update the stay reasons via checkboxes, max of 3
	const handleCheckboxChange = evt => {
		const { name, checked } = evt.target;

		setArrivalDetailsValues(prev => {
			if (checked && prev.stayReasonIds.length < 3) {
				return {
					...prev,
					stayReasonIds: [...prev.stayReasonIds, name],
				};
			}
			if (!checked) {
				return {
					...prev,
					stayReasonIds: prev.stayReasonIds.filter(id => id !== name),
				};
			}
			return prev;
		});
	};

	const handleCommentChange = newComment => {
		setArrivalDetailsValues(prev => ({
			...prev,
			comments: newComment,
		}));
	};

	// If guest has atleast 1 of the arrival details, show the cards
	const hasArrivalDetails = [
		preArrivalPreferences?.guestEta,
		preArrivalPreferences?.comments,
		preArrivalPreferences?.stayReasonIds.length > 0,
	].some(a => a);

	const numberOfAdults = upcomingTrip?.hotelProducts[0]?.guestCount?.numberAdults;

	const preArrivalPreferencesGuests = preArrivalPreferences?.guests || [];
	const preArrivalPreferencesGuestsAdultsCount = preArrivalPreferencesGuests.filter(
		({ isMinor }) => !isMinor
	).length;

	const emptyGuestFieldsNeeded = numberOfAdults - preArrivalPreferencesGuestsAdultsCount;

	const emptyGuestFields = Array.from({ length: emptyGuestFieldsNeeded }, () => ({
		createdByGuest: true,
		isMinor: false,
		firstName: '',
		lastName: '',
		birthYear: null,
	}));

	const onInitialFormSubmit = guestInfo => {
		const filteredGuestInfo = guestInfo.filter(
			({ firstName, isMinor, birthYear }) => firstName && (!isMinor || birthYear)
		);

		setArrivalDetailsValues(prev => ({
			...prev,
			guests: filteredGuestInfo,
		}));

		const newValues = {
			...arrivalDetailsValues,
			guestEta: `${arrivalDetailsValues.guestEta} ${upcomingTrip.startDate}`,
			guests: filteredGuestInfo,
			bookingId: upcomingTrip.bookingId,
		};

		dispatch(updatePreArrivalPreference(newValues));
		dispatch(
			arrivalDetailsFormCompleted({
				booking: upcomingTrip,
				arrivalDetailsValues: newValues,
				stayReasonsAllOptions,
				profile,
			})
		);
	};

	const isUpdating = useSelector(state => state.preArrivalPreference.isUpdating);

	if (isUpdating) {
		return <BookingFlowLoadingIndicator />;
	}

	if (hasArrivalDetails) {
		return (
			<ArrivalDetailCards
				preArrivalPreferences={preArrivalPreferences}
				emptyGuestFields={emptyGuestFields}
				stayReasons={stayReasons}
				stayReasonsAllOptions={stayReasonsAllOptions}
				upcomingTrip={upcomingTrip}
				propertyContent={propertyContent}
				profile={profile}
			/>
		);
	}

	if (
		preArrivalPreferencesRes?.success === undefined ||
		preArrivalPreferencesRes?.message === '13 INTERNAL: Failed to find booking in AXP'
	) {
		return null;
	}

	return (
		<div className="fs-trip__arrival-details">
			<div className="fs-trip__arrival-details__container">
				<h2 className="fs-trip__arrival-details__title">Arrival Details</h2>
				{preArrivalPreferencesRes?.success === false && formStep === 2 && (
					<div style={{ color: 'red' }}>
						{t('An error occurred while submitting your arrival details.')}
					</div>
				)}

				{formStep === 0 && (
					<TimeOfArrival
						checkInTimeFormatted={formatArrivalTime(arrivalDetailsValues.guestEta)}
						arrivalTimeValue={arrivalDetailsValues.guestEta}
						setArrivalDetailsValues={setArrivalDetailsValues}
						goToFormStep={goToFormStep}
					/>
				)}
				{formStep === 1 && (
					<>
						<p className="fs-trip__arrival-details__subtitle">
							{t(
								'Celebrating a special occasion? We will gladly assist with preparing a special surprise for your companion or family member.'
							)}
						</p>
						<ReasonForStay
							stayReasons={stayReasons}
							selectedStayReasonIds={arrivalDetailsValues.stayReasonIds}
							comments={arrivalDetailsValues.comments}
							onCheckboxChange={handleCheckboxChange}
							onCommentsChange={handleCommentChange}
							onGoBack={() => goToFormStep(0)}
							onSubmit={() => goToFormStep(2)}
						/>
					</>
				)}
				{formStep === 2 && (
					<>
						<p className="fs-trip__arrival-details__subtitle">
							{t('Please provide all guest information.')}
						</p>
						<GuestInformation
							propertyContent={propertyContent}
							guestInfoValues={[...preArrivalPreferencesGuests, ...emptyGuestFields]}
							onGoBack={() => goToFormStep(1)}
							onSubmit={onInitialFormSubmit}
						/>
					</>
				)}

				<div className="fs-trip__arrival-details__page-num">{`${formStep + 1} / 3`}</div>
			</div>
		</div>
	);
}
